/**
 * Shared Pipedrive Utilities
 * Common utility functions for Pipedrive integration across all apps
 */

import { PIPEDRIVE_CUSTOM_FIELDS } from './pipedrive-config';
import type { PipedriveDealData } from './pipedrive-types';

/**
 * Convert deal data with custom fields to Pipedrive API format
 */
export function convertDealDataToPipedriveFormat(dealData: PipedriveDealData): Record<string, unknown> {
  const pipedriveData: Record<string, unknown> = {
    title: dealData.title,
    value: dealData.value,
    currency: dealData.currency || 'USD',
    person_id: dealData.person_id,
    org_id: dealData.org_id,
    stage_id: dealData.stage_id,
    status: dealData.status || 'open',
    probability: dealData.probability,
    visible_to: dealData.visible_to || '3',
  };

  // Add custom fields
  const customFields: Record<string, unknown> = {};
  
  if (dealData.clientLocation) {
    customFields.clientLocation = dealData.clientLocation;
  }
  if (dealData.dealType) {
    customFields.dealType = dealData.dealType;
  }
  if (dealData.speakerType) {
    customFields.speakerType = dealData.speakerType;
  }
  if (dealData.sessionTopic) {
    customFields.sessionTopic = dealData.sessionTopic;
  }
  if (dealData.proposalLink) {
    customFields.proposalLink = dealData.proposalLink;
  }
  if (dealData.proposedEventDate) {
    customFields.proposedEventDate = dealData.proposedEventDate;
  }

  // Convert custom fields to Pipedrive format using field IDs
  if (Object.keys(customFields).length > 0) {
    for (const [fieldName, value] of Object.entries(customFields)) {
      const fieldId = PIPEDRIVE_CUSTOM_FIELDS.deal[fieldName as keyof typeof PIPEDRIVE_CUSTOM_FIELDS.deal]?.id;
      if (fieldId) {
        pipedriveData[fieldId] = value;
      }
    }
  }

  return pipedriveData;
}

/**
 * Extract custom field values from Pipedrive deal data
 */
export function extractCustomFieldsFromDeal(dealData: Record<string, unknown>): Record<string, unknown> {
  const customFields: Record<string, unknown> = {};
  
  // Map field IDs back to field names
  const fieldIdToName: Record<string, string> = {};
  for (const [fieldName, fieldConfig] of Object.entries(PIPEDRIVE_CUSTOM_FIELDS.deal)) {
    fieldIdToName[fieldConfig.id] = fieldName;
  }
  
  // Extract custom field values
  for (const [key, value] of Object.entries(dealData)) {
    if (fieldIdToName[key]) {
      customFields[fieldIdToName[key]] = value;
    }
  }
  
  return customFields;
}

/**
 * Validate deal data before sending to Pipedrive
 */
export function validateDealData(dealData: PipedriveDealData): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!dealData.title || dealData.title.trim() === '') {
    errors.push('Deal title is required');
  }
  
  if (dealData.value !== undefined && dealData.value < 0) {
    errors.push('Deal value cannot be negative');
  }
  
  if (dealData.probability !== undefined && (dealData.probability < 0 || dealData.probability > 100)) {
    errors.push('Deal probability must be between 0 and 100');
  }
  
  if (dealData.status && !['open', 'won', 'lost'].includes(dealData.status)) {
    errors.push('Deal status must be one of: open, won, lost');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Format deal value for display
 */
export function formatDealValue(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(value);
}

/**
 * Get deal status color for UI
 */
export function getDealStatusColor(status: string): string {
  switch (status) {
    case 'won':
      return 'green';
    case 'lost':
      return 'red';
    case 'open':
    default:
      return 'blue';
  }
}

/**
 * Calculate deal age in days
 */
export function calculateDealAge(addTime: string): number {
  const addDate = new Date(addTime);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - addDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Check if deal is stale (older than specified days)
 */
export function isDealStale(addTime: string, staleThresholdDays: number = 30): boolean {
  return calculateDealAge(addTime) > staleThresholdDays;
}
